import React from 'react'
import { withTranslation } from 'react-i18next'
import * as UIcons from '@iconscout/react-unicons'
import dayjs from 'dayjs'

function SectionCard({ title, editLabel, onEdit, children }) {
  return (
    <div className="border border-border-lines rounded-xl p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-dark-blue">{title}</p>
        <button type="button" onClick={onEdit} className="flex items-center gap-1 text-sm font-medium text-primary">
          <UIcons.UilEditAlt size={14} /> {editLabel}
        </button>
      </div>
      {children}
    </div>
  )
}

function Row({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-sm text-secondary shrink-0 w-40">{label}</span>
      <span className="text-sm text-dark-blue text-right flex-1">{children}</span>
    </div>
  )
}

function ChipRow({ label, values }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-sm text-secondary shrink-0 w-40">{label}</span>
      <div className="flex flex-1 flex-wrap justify-end gap-1.5">
        {
          values.length
            ? values.map((value) => (
              <span key={value} className="bg-background rounded-full px-2.5 py-0.5 text-xs font-medium text-dark-blue">{value}</span>
            ))
            : <span className="text-sm text-secondary">--</span>
        }
      </div>
    </div>
  )
}

const DAY_LABELS = { mon: 'MON', tue: 'TUE', wed: 'WED', thu: 'THU', fri: 'FRI', sat: 'SAT', sun: 'SUN' }

export default withTranslation()(function ReviewStep({ t, form, file, onEditStep }) {
  const f = t('rewardAds.createFlow.review.fields', { returnObjects: true })
  const editLabel = t('rewardAds.createFlow.review.edit')

  return (
    <div className="flex flex-col gap-6 max-w-[640px]">
      <h2 className="text-xl font-semibold text-dark-blue">{t('rewardAds.createFlow.review.title')}</h2>

      <SectionCard title={t('rewardAds.createFlow.review.sections.basic_details')} editLabel={editLabel} onEdit={() => onEditStep(0)}>
        <Row label={f.title}>{form.title || '--'}</Row>
        <Row label={f.callback_url}>{form.callbackUrl || '--'}</Row>
        <Row label={f.cta_message}>{form.ctaMessage || '--'}</Row>
        <Row label={f.description}>{form.description || '--'}</Row>
      </SectionCard>

      <SectionCard title={t('rewardAds.createFlow.review.sections.targeting')} editLabel={editLabel} onEdit={() => onEditStep(1)}>
        <ChipRow label={f.interest} values={form.interests} />
        <ChipRow label={f.location} values={form.locations} />
        <ChipRow label={f.ip_targeting} values={form.ipRanges} />
      </SectionCard>

      <SectionCard title={t('rewardAds.createFlow.review.sections.budgeting')} editLabel={editLabel} onEdit={() => onEditStep(2)}>
        <Row label={f.daily_budget}>{form.dailyBudget ? `NGN ${Number(form.dailyBudget).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '--'}</Row>
        <Row label={f.start_date}>{form.startDate ? dayjs(form.startDate).format('DD MMMM YYYY') : '--'}</Row>
        <Row label={f.end_date}>{form.endDate ? dayjs(form.endDate).format('DD MMMM YYYY') : '--'}</Row>
        <ChipRow label={f.days_to_display} values={form.daysToDisplay.map((key) => DAY_LABELS[key])} />
        <Row label={f.hours_to_display}>{form.startTime && form.endTime ? `${form.startTime} - ${form.endTime}` : '--'}</Row>
        <Row label={f.frequency}>{form.frequency || '--'}</Row>
        <Row label={f.schedules}>{form.scheduleIds.length ? t('rewardAds.createFlow.review.schedules_selected', { count: form.scheduleIds.length }) : '--'}</Row>
        <Row label={f.max_views}>{form.maxViewsPerUserDay ? `${form.maxViewsPerUserDay} views per user per day` : '--'}</Row>
      </SectionCard>

      <SectionCard title={t('rewardAds.createFlow.review.sections.creative_upload')} editLabel={editLabel} onEdit={() => onEditStep(3)}>
        <Row label={f.upload}>{file?.name || '--'}</Row>
        <Row label={f.size}>{file ? `${(file.size / (1024 * 1024)).toFixed(1)} MB` : '--'}</Row>
      </SectionCard>
    </div>
  )
})
